import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isDomainBlocked } from "@/data/blockedDomains";
import { rateLimiter } from "@/utils/rateLimit";
import Spinner from "@/components/ui/spinner";
const Grad2 = "/login_page/login_page_oreog_illustration_01.png";
const Grad3 = "/login_page/login_page_oreog_illustration_04.png";
const Grad4 = "/login_page/login_page_oreog_illustration_03.png";
const authBg = "/login_page/login_page_oreog_illustration_06.png";
import EmailConfirmationModal from "@/components/EmailConfirmationModal";

type FormType = "signin" | "signup" | "forgot";

export default function AuthNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signUp, signIn, signInWithGoogle, resetPassword } =
    useSupabase();
  const { toast } = useToast();

  // Check if dark mode is enabled
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setIsDarkMode(isDark);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const isDark = document.documentElement.classList.contains("dark");
          setIsDarkMode(isDark);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formType, setFormType] = useState<FormType>("signin");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    confirmPassword: "",
  });
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState("");

  const [rateLimitInfo, setRateLimitInfo] = useState<{
    blocked: boolean;
    timeRemaining: number;
    attemptsRemaining: number;
  }>({ blocked: false, timeRemaining: 0, attemptsRemaining: 5 });

  // Turnstile state
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const widgetIdRef = useRef<string | null>(null);

  // Carousel state for left image panel (md+ only)
  const carouselImages = [Grad2, Grad3, Grad4];
  const [currentImage, setCurrentImage] = useState(0);
  const carouselIntervalRef = useRef<number | null>(null);
  const resumeTimeoutRef = useRef<number | null>(null);
  const dragStartXRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  const goToNextImage = useCallback(() => {
    setCurrentImage((prev) => (prev + 1) % carouselImages.length);
  }, [carouselImages.length]);

  const goToPreviousImage = useCallback(() => {
    setCurrentImage((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, [carouselImages.length]);

  const stopCarouselAutoPlay = useCallback(() => {
    if (carouselIntervalRef.current !== null) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = null;
    }
  }, []);

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current !== null) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const startCarouselAutoPlay = useCallback(() => {
    stopCarouselAutoPlay();
    carouselIntervalRef.current = window.setInterval(goToNextImage, 3000);
  }, [goToNextImage, stopCarouselAutoPlay]);

  const scheduleAutoPlayResume = useCallback(() => {
    clearResumeTimeout();
    resumeTimeoutRef.current = window.setTimeout(() => {
      startCarouselAutoPlay();
    }, 3500);
  }, [clearResumeTimeout, startCarouselAutoPlay]);

  useEffect(() => {
    startCarouselAutoPlay();
    return () => {
      stopCarouselAutoPlay();
      clearResumeTimeout();
    };
  }, [startCarouselAutoPlay, stopCarouselAutoPlay, clearResumeTimeout]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    stopCarouselAutoPlay();
    clearResumeTimeout();
    isDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch { }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || dragStartXRef.current === null) {
      return;
    }
    const deltaX = event.clientX - dragStartXRef.current;
    if (Math.abs(deltaX) > 16) {
      event.preventDefault();
    }
  };

  const endPointerInteraction = (
    event: React.PointerEvent<HTMLDivElement>,
    cancelled = false
  ) => {
    if (!isDraggingRef.current) {
      return;
    }

    const startX = dragStartXRef.current;
    isDraggingRef.current = false;
    dragStartXRef.current = null;

    if (!cancelled && startX !== null) {
      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 40) {
        if (deltaX > 0) {
          goToPreviousImage();
        } else {
          goToNextImage();
        }
      }
    }

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch { }
    scheduleAutoPlayResume();
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    endPointerInteraction(event);
  };

  const handlePointerLeave = (event: React.PointerEvent<HTMLDivElement>) => {
    endPointerInteraction(event, true);
  };

  const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    endPointerInteraction(event, true);
  };

  const handleDotClick = (index: number) => {
    stopCarouselAutoPlay();
    clearResumeTimeout();
    setCurrentImage(index);
    scheduleAutoPlayResume();
  };

  // Turnstile callbacks
  const onTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const onTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const onTurnstileError = useCallback(() => {
    setTurnstileToken("");
  }, []);

  // Render Turnstile widget when form type changes
  useEffect(() => {
    const renderTurnstile = () => {
      if (!turnstileRef.current) return;

      const turnstile = (window as any).turnstile;
      if (!turnstile) return;

      // Remove existing widget if any
      if (widgetIdRef.current) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.error('Error removing Turnstile widget:', e);
        }
      }

      // Clear token
      setTurnstileToken("");

      // Get site key from environment variable
      const sitekey = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAACJOHYc7dzDTXqyB";

      // Render new widget
      try {
        widgetIdRef.current = turnstile.render(turnstileRef.current, {
          sitekey,
          callback: onTurnstileVerify,
          "expired-callback": onTurnstileExpire,
          "error-callback": onTurnstileError,
          theme: "light",
          size: "normal",
        });
      } catch (e) {
        console.error('Error rendering Turnstile widget:', e);
      }
    };

    // Wait for Turnstile script to load
    const checkTurnstile = setInterval(() => {
      if ((window as any).turnstile) {
        clearInterval(checkTurnstile);
        renderTurnstile();
      }
    }, 100);

    // Cleanup timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkTurnstile);
    }, 10000);

    return () => {
      clearInterval(checkTurnstile);
      clearTimeout(timeout);
      if (widgetIdRef.current && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.error('Error cleaning up Turnstile widget:', e);
        }
      }
    };
  }, [formType, onTurnstileVerify, onTurnstileExpire, onTurnstileError]);

  // Password validation function
  const getPasswordValidation = (password: string) => {
    return {
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  // Field uniqueness validation function
  const validateFieldUniqueness = () => {
    const { fullName, email, password } = formData;
    const issues = [];

    if (fullName && email && fullName.toLowerCase() === email.toLowerCase()) {
      issues.push("Name cannot be the same as email");
    }
    if (
      fullName &&
      password &&
      fullName.toLowerCase() === password.toLowerCase()
    ) {
      issues.push("Name cannot be the same as password");
    }
    if (email && password && email.toLowerCase() === password.toLowerCase()) {
      issues.push("Email cannot be the same as password");
    }

    return issues;
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      const redirectTo = location.state?.from?.pathname || "/";
      navigate(redirectTo, { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  const updateRateLimitInfo = (action: "signin" | "signup") => {
    if (!formData.email) return;

    const blocked = rateLimiter.isBlocked(formData.email, action);
    const timeRemaining = rateLimiter.getBlockedTimeRemaining(
      formData.email,
      action
    );
    const attemptsRemaining = rateLimiter.getRemainingAttempts(
      formData.email,
      action
    );

    setRateLimitInfo({ blocked, timeRemaining, attemptsRemaining });
  };

  // Removed hCaptcha callbacks

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // Update rate limit info when email changes
    if (e.target.name === "email" && e.target.value) {
      setTimeout(() => {
        updateRateLimitInfo(formType === "signin" ? "signin" : "signup");
      }, 100);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check Turnstile verification
    if (!turnstileToken) {
      toast({
        title: "Verification required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
      return;
    }

    // Check rate limiting
    if (rateLimiter.isBlocked(formData.email, "signup")) {
      const timeRemaining = rateLimiter.getBlockedTimeRemaining(
        formData.email,
        "signup"
      );
      toast({
        title: "Too many attempts",
        description: ``,
        variant: "destructive",
      });
      return;
    }

    // Check if email domain is blocked
    if (isDomainBlocked(formData.email)) {
      toast({
        title: "Email not allowed",
        description: "This email domain is not permitted for registration",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    // Validate field uniqueness
    const uniquenessIssues = validateFieldUniqueness();
    if (uniquenessIssues.length > 0) {
      toast({
        title: "Field validation failed",
        description: uniquenessIssues[0], // Show the first issue
        variant: "destructive",
      });
      return;
    }

    // Validate all password requirements
    const validation = getPasswordValidation(formData.password);
    const isPasswordValid =
      validation.minLength &&
      validation.hasUppercase &&
      validation.hasLowercase &&
      validation.hasNumber &&
      validation.hasSpecialChar;

    if (!isPasswordValid) {
      toast({
        title: "Password requirements not met",
        description:
          "Please ensure your password meets all the requirements shown below",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signUp(
        formData.email,
        formData.password,
        formData.fullName,
        turnstileToken
      );

      if (error) {
        // Record failed attempt
        rateLimiter.recordAttempt(formData.email, "signup", false);
        updateRateLimitInfo("signup");

        toast({
          title: "Sign up failed",
          description: error.message || "Failed to create account",
          variant: "destructive",
        });
      } else {
        // Record successful attempt
        rateLimiter.recordAttempt(formData.email, "signup", true);
        updateRateLimitInfo("signup");

        // Open email confirmation modal instead of toast
        setSignedUpEmail(formData.email);
        setShowEmailConfirmation(true);
        setFormType("signin");
      }
    } catch (error: any) {
      // Record failed attempt
      rateLimiter.recordAttempt(formData.email, "signup", false);
      updateRateLimitInfo("signup");

      toast({
        title: "Sign up failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check Turnstile verification
    if (!turnstileToken) {
      toast({
        title: "Verification required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
      return;
    }

    // Check rate limiting
    if (rateLimiter.isBlocked(formData.email, "signin")) {
      const timeRemaining = rateLimiter.getBlockedTimeRemaining(
        formData.email,
        "signin"
      );
      toast({
        title: "Too many attempts",
        description: `Please wait ${rateLimiter.formatBlockedTime(
          timeRemaining
        )} before trying again`,
        variant: "destructive",
      });
      return;
    }
    // Check if email domain is blocked
    if (isDomainBlocked(formData.email)) {
      toast({
        title: "Email not allowed",
        description: "This email domain is not permitted for login",
        variant: "destructive",
      });
      return;
    }

    // Validate field uniqueness (for signin, only check email vs password)
    if (
      formData.email &&
      formData.password &&
      formData.email.toLowerCase() === formData.password.toLowerCase()
    ) {
      toast({
        title: "Field validation failed",
        description: "Email cannot be the same as password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signIn(
        formData.email,
        formData.password,
        turnstileToken
      );

      if (error) {
        // Record failed attempt
        rateLimiter.recordAttempt(formData.email, "signin", false);
        updateRateLimitInfo("signin");

        toast({
          title: "Login failed",
          description: error.message || "Invalid email or password",
          variant: "destructive",
        });
      } else {
        // Record successful attempt
        rateLimiter.recordAttempt(formData.email, "signin", true);
        updateRateLimitInfo("signin");
      }
    } catch (error: any) {
      // Record failed attempt
      rateLimiter.recordAttempt(formData.email, "signin", false);
      updateRateLimitInfo("signin");

      toast({
        title: "Login failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check Turnstile verification
    if (!turnstileToken) {
      toast({
        title: "Verification required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await resetPassword(formData.email);

      if (error) {
        toast({
          title: "Reset failed",
          description: error.message || "Failed to send reset email",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Check your email",
          description: "We've sent you a password reset link",
        });
        setFormType("signin");
      }
    } catch (error: any) {
      toast({
        title: "Reset failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const { error } = await signInWithGoogle();

      if (error) {
        toast({
          title: "Google login failed",
          description: error.message || "Failed to login with Google",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Google login failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div
      className={'auth-page min-h-screen flex items-center justify-center relative overflow-hidden'}
    >
      {/* Background image */}
      <img
        src={authBg}
        alt="Auth background"
        className="absolute bg-sky-300/90 inset-0 -z-10 w-full h-full object-cover pointer-events-none select-none"
      />
      {/* Lighten bottom-right dark patch overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-white/10 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 right-0 -z-10 w-1/3 h-1/3 bg-white/20 blur-2xl pointer-events-none" />
      {/* Card container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto my-6 md:my-12 px-2 md:px-4">
        <div className="flex flex-col md:flex-row rounded-[28px] overflow-hidden bg-white/95 border border-10 border-gray-200 backdrop-blur-xl shadow-2xl">
          {/* Left image panel */}
          <div
            className="relative hidden md:block md:w-1/2 md:h-[600px] select-none pt-8 px-4 cursor-grab active:cursor-grabbing touch-pan-y"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            onPointerCancel={handlePointerCancel}
          >
            {/* Carousel images */}
            {carouselImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Auth visual ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 pl-2 py-2 rounded-[30px] overflow-hidden ${currentImage === idx ? "opacity-100" : "opacity-0"
                  }`}
                draggable={false}
              />
            ))}
            {/* Dark overlay */}
            <div className="hidden bg-transparent" />
            {/* Dots indicator - visible */}
            <div className="absolute bottom-4 left-2.5 z-10">
              <div className="flex items-center gap-3 rounded-full px-3 py-2">
                {carouselImages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleDotClick(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`${i === currentImage
                        ? "w-20 bg-white"
                        : "w-8 bg-white/60"
                      } h-1.5 rounded-full transition-all focus:outline-6 focus:ring-0 focus:ring-white/70`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right form panel */}
          <div className="md:w-1/2 w-full flex flex-col items-center justify-center p-4 xs:p-6 md:p-6 shadow-xl">
            {/* Sign In Form */}

            {/* Sign In Form */}
            {formType === "signin" && (
              <div
                className={`w-full max-w-sm rounded-2xl py-6 px-1 xs:px-2 bg-transparent shadow-none`}
              >
                <form
                  onSubmit={handleSignIn}
                  className="flex flex-col items-center justify-center"
                >
                  <h2 className="text-4xl font-medium text-gray-900 instrument-serif-regular">
                    Login
                  </h2>
                  <p className="text-sm my-3 text-gray-600">
                    Welcome back! Please login to continue
                  </p>

                  {/* <button 
                type="button" 
                onClick={handleGoogleAuth}
                disabled={isLoading}
                className={`w-full mt-8 flex items-center justify-center h-12 rounded-full transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800/50' 
                    : 'bg-gray-500/10 hover:bg-gray-500/20 disabled:bg-gray-500/5'
                }`}
              >
                <img src="https://raw.githubusercontent.com/prebuiltui/prebuiltui/main/assets/login/googleLogo.svg" alt="Google" />
              </button> */}

                  {/* <div className="flex items-center gap-4 w-full my-5">
                <div className={`w-full h-px ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-300/90'}`}></div>
                <p className={`w-full text-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500/90'}`}>
                  or sign in with email
                </p>
                <div className={`w-full h-px ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-300/90'}`}></div>
              </div> */}

                  <div
                    className={`flex items-center w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border transition-all duration-200 bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10 hover:border-emerald-400/60`}
                  >
                    <svg
                      width="16"
                      height="11"
                      viewBox="0 0 16 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 .55.571 0H15.43l.57.55v9.9l-.571.55H.57L0 10.45zm1.143 1.138V9.9h13.714V1.69l-6.503 4.8h-.697zM13.749 1.1H2.25L8 5.356z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email id"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full text-gray-900`}
                      required
                    />
                  </div>

                  <div
                    className={`flex items-center mt-6 w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border relative bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10`}
                  >
                    <svg
                      width="13"
                      height="17"
                      viewBox="0 0 13 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 8.5c0-.938-.729-1.7-1.625-1.7h-.812V4.25C10.563 1.907 8.74 0 6.5 0S2.438 1.907 2.438 4.25V6.8h-.813C.729 6.8 0 7.562 0 8.5v6.8c0 .938.729 1.7 1.625 1.7h9.75c.896 0 1.625-.762 1.625-1.7zM4.063 4.25c0-1.406 1.093-2.55 2.437-2.55s2.438 1.144 2.438 2.55V6.8H4.061z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="current-password"
                      data-theme={"light"}
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full pr-10 text-gray-900`}
                      required
                    />
                    {formData.password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-4 text-gray-500 hover:text-gray-700`}
                      >
                        {showPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>

                  <div
                    className={`w-full flex items-center justify-between mt-3 text-gray-600`}
                  >
                    <button
                      type="button"
                      onClick={() => setFormType("forgot")}
                      className="text-sm underline hover:no-underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  {/* Turnstile Widget */}
                  <div className="w-full mt-4 flex justify-center">
                    <div ref={turnstileRef} />
                  </div>

                  {/* Rate Limiting Info */}
                  {formData.email &&
                    rateLimitInfo.attemptsRemaining < 5 &&
                    rateLimitInfo.attemptsRemaining > 0 && (
                      ""
                    )}

                  <button
                    type="submit"
                    disabled={
                      isLoading || rateLimitInfo.blocked || !turnstileToken
                    }
                    className="mt-8 w-full h-11 rounded-full text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
                  >
                    {isLoading && <Spinner size="sm" className="text-white" />}
                    {isLoading
                      ? "Signing in..."
                      : rateLimitInfo.blocked
                        ? "Too many attempts"
                        : "Login"}
                  </button>

                  <p className={`text-sm mt-4 text-gray-600`}>
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setFormType("signup");
                      }}
                      className="text-emerald-700 hover:underline"
                    >
                      Sign up
                    </button>
                  </p>
                </form>
              </div>
            )}

            {/* Sign Up Form */}
            {formType === "signup" && (
              <div
                className={`w-full max-w-sm rounded-2xl py-4 px-4 xs:p-6 bg-transparent`}
              >
                <form
                  onSubmit={handleSignUp}
                  className="flex flex-col items-center justify-center"
                >
                  <h2 className="text-4xl font-medium text-gray-900 instrument-serif-regular">
                    Sign up
                  </h2>
                  <p className="text-sm mt-3 mb-4 text-gray-600">
                    Create your account to get started
                  </p>

                  {/* <button 
                type="button" 
                onClick={handleGoogleAuth}
                disabled={isLoading}
                className={`w-full mt-8 flex items-center justify-center h-12 rounded-full transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800/50' 
                    : 'bg-gray-500/10 hover:bg-gray-500/20 disabled:bg-gray-500/5'
                }`}
              >
                <img src="https://raw.githubusercontent.com/prebuiltui/prebuiltui/main/assets/login/googleLogo.svg" alt="Google" />
              </button> */}

                  {/* <div className="flex items-center gap-4 w-full my-5">
                <div className={`w-full h-px ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-300/90'}`}></div>
                <p className={`w-full text-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500/90'}`}>
                  or create account with email
                </p>
                <div className={`w-full h-px ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-300/90'}`}></div>
              </div> */}

                  {/* Full Name Input */}
                  <div
                    className={`flex items-center w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border transition-all duration-200 bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10 hover:border-emerald-400/60`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                        stroke={isDarkMode ? "#6B7280" : "#6B7280"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="7"
                        r="4"
                        stroke={isDarkMode ? "#6B7280" : "#6B7280"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Full Name"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      autoComplete="name"
                      data-theme={"light"}
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full text-gray-900`}
                      required
                    />
                  </div>

                  {/* Email Input */}
                  <div
                    className={`flex items-center mt-4 w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10`}
                  >
                    <svg
                      width="16"
                      height="11"
                      viewBox="0 0 16 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 .55.571 0H15.43l.57.55v9.9l-.571.55H.57L0 10.45zm1.143 1.138V9.9h13.714V1.69l-6.503 4.8h-.697zM13.749 1.1H2.25L8 5.356z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email id"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      data-theme={"light"}
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full text-gray-900`}
                      required
                    />
                  </div>

                  {/* Password Input */}
                  <div
                    className={`flex items-center mt-4 w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border relative bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10`}
                  >
                    <svg
                      width="13"
                      height="17"
                      viewBox="0 0 13 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 8.5c0-.938-.729-1.7-1.625-1.7h-.812V4.25C10.563 1.907 8.74 0 6.5 0S2.438 1.907 2.438 4.25V6.8h-.813C.729 6.8 0 7.562 0 8.5v6.8c0 .938.729 1.7 1.625 1.7h9.75c.896 0 1.625-.762 1.625-1.7zM4.063 4.25c0-1.406 1.093-2.55 2.437-2.55s2.438 1.144 2.438 2.55V6.8H4.061z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      data-theme={isDarkMode ? "dark" : "light"}
                      className={`bg-transparent placeholder-gray-600 outline-none text-sm w-full h-full pr-10 text-gray-900`}
                      required
                      minLength={6}
                    />
                    {formData.password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-4 ${isDarkMode
                            ? "text-gray-400 hover:text-gray-300"
                            : "text-gray-500 hover:text-gray-700"
                          }`}
                      >
                        {showPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Password Requirements */}
                  {formData.password && (
                    <div className="w-full mt-3 px-2">
                      <div className="grid grid-cols-1 gap-1 text-xs">
                        {(() => {
                          const validation = getPasswordValidation(
                            formData.password
                          );
                          const requirements = [
                            {
                              key: "minLength",
                              text: "At least 6 characters",
                              isValid: validation.minLength,
                            },
                            {
                              key: "hasUppercase",
                              text: "One uppercase letter",
                              isValid: validation.hasUppercase,
                            },
                            {
                              key: "hasLowercase",
                              text: "One lowercase letter",
                              isValid: validation.hasLowercase,
                            },
                            {
                              key: "hasNumber",
                              text: "One number",
                              isValid: validation.hasNumber,
                            },
                            {
                              key: "hasSpecialChar",
                              text: "One special character",
                              isValid: validation.hasSpecialChar,
                            },
                          ];

                          // Only show unmet requirements
                          const unmetRequirements = requirements.filter(
                            (req) => !req.isValid
                          );

                          return unmetRequirements.map((req) => (
                            <div
                              key={req.key}
                              className={`flex items-center gap-2 text-red-500`}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                              <span>{req.text}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Confirm Password Input */}
                  <div
                    className={`flex items-center mt-4 w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border relative bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10 hover:border-emerald-400/60`}
                  >
                    <svg
                      width="13"
                      height="17"
                      viewBox="0 0 13 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 8.5c0-.938-.729-1.7-1.625-1.7h-.812V4.25C10.563 1.907 8.74 0 6.5 0S2.438 1.907 2.438 4.25V6.8h-.813C.729 6.8 0 7.562 0 8.5v6.8c0 .938.729 1.7 1.625 1.7h9.75c.896 0 1.625-.762 1.625-1.7zM4.063 4.25c0-1.406 1.093-2.55 2.437-2.55s2.438 1.144 2.438 2.55V6.8H4.061z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      data-theme={"light"}
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full pr-10 text-gray-900`}
                      required
                    />
                    {formData.confirmPassword && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-4 text-gray-500 hover:text-gray-700`}
                      >
                        {showPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Turnstile Widget */}
                  <div className="w-full mt-4 flex justify-center">
                    <div ref={turnstileRef} />
                  </div>

                  {/* Rate Limiting Info */}
                  {/* {formData.email &&
                    rateLimitInfo.attemptsRemaining < 5 &&
                    rateLimitInfo.attemptsRemaining > 0 && (
                      <div
                        className={`w-full mt-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200`}
                      >
                        <p className={`text-xs text-yellow-800`}>
                          ⚠️ {rateLimitInfo.attemptsRemaining} signup attempts
                          remaining
                        </p>
                      </div>
                    )} */}

                  <button
                    type="submit"
                    disabled={
                      isLoading || rateLimitInfo.blocked || !turnstileToken
                    }
                    className="mt-8 w-full h-11 rounded-full text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
                  >
                    {isLoading && <Spinner size="sm" className="text-white" />}
                    {isLoading
                      ? "Creating account..."
                      : rateLimitInfo.blocked
                        ? "Too many attempts"
                        : "Create Account"}
                  </button>

                  <p className={`text-sm mt-4 text-gray-600`}>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setFormType("signin");
                      }}
                      className="text-emerald-700 hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </div>
            )}

            {/* Forgot Password Form */}
            {formType === "forgot" && (
              <div
                className={`w-full max-w-sm rounded-2xl sm:px-8 py-6 px-1 xs:px-2 bg-transparent`}
              >
                <form
                  onSubmit={handleForgotPassword}
                  className="flex flex-col items-center justify-center"
                >
                  <h2 className="text-4xl font-medium text-gray-900 instrument-serif-regular">
                    Reset Password
                  </h2>
                  <p className="text-sm mt-3 text-gray-600">
                    Enter your email to receive a reset link
                  </p>

                  <div
                    className={`flex items-center mt-8 w-full h-12 rounded-full overflow-hidden pl-6 gap-2 border bg-white border-gray-300 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/10`}
                  >
                    <svg
                      width="16"
                      height="11"
                      viewBox="0 0 16 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 .55.571 0H15.43l.57.55v9.9l-.571.55H.57L0 10.45zm1.143 1.138V9.9h13.714V1.69l-6.503 4.8h-.697zM13.749 1.1H2.25L8 5.356z"
                        fill={isDarkMode ? "#6B7280" : "#6B7280"}
                      />
                    </svg>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email id"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-theme={"light"}
                      className={`bg-transparent placeholder-gray-500 outline-none text-sm w-full h-full text-gray-900`}
                      required
                    />
                  </div>

                  {/* Turnstile Widget */}
                  <div className="w-full mt-4 flex justify-center">
                    <div ref={turnstileRef} />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !turnstileToken}
                    className="mt-8 w-full h-11 rounded-full text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors"
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </button>

                  <p className={`text-sm mt-4 text-gray-600`}>
                    Remember your password?{" "}
                    <button
                      type="button"
                      onClick={() => setFormType("signin")}
                      className="text-emerald-700 hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Email Confirmation Modal */}
      <EmailConfirmationModal
        isOpen={showEmailConfirmation}
        onClose={() => setShowEmailConfirmation(false)}
        email={signedUpEmail}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
